import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Plus, Clock, ChevronRight, AlertTriangle, Download, Send, Wallet, ClipboardCheck, Users, Search } from 'lucide-react';
import {
  PageHeader, Panel, Button, Pill, RowMenu, Empty, Avatar, SearchInput, LoadingBlock, ErrorBlock, money, useLoad, fmtDate, words,
} from '../kit';
import { toCsv } from '../../../utils/csv';
import { useLogTime } from '../workflow/LogTimeProvider';
import { SCModal, BulkMessageModal, BudgetCalculatorModal, SupervisionModal } from './modals';
import { ViewPicker, fundingLabel, fullName, daysFromToday, agoText, inText } from './shared';

// Caseload: every participant with what a coordinator scans for (when the plan ends, how long since
// the last note, budget left, who coordinates them) and a Log time on each row. Colour marks only
// what is late: no note for 30 days, a budget overspent, a progress report overdue.

const live = (sc) => sc.is_active && sc.status === 'active';
const noteAge = (sc) => (sc.last_note ? -daysFromToday(sc.last_note) : null);

// What needs a look on one participant, worst first. `late` items are red; the rest amber. Items
// marked `col` are already visible in a column (last note, budget left), so the row's alert line
// leaves them out; they still put the participant in the Needs attention view.
function attention(sc) {
  const out = [];
  const isLive = live(sc);
  const report = daysFromToday(sc.progress_report_due);
  const review = daysFromToday(sc.plan_review_due);
  const age = noteAge(sc);
  const bal = Number(sc.total_balance) || 0;
  const budget = Number(sc.total_budget) || 0;
  if (report != null && report < 0) out.push({ late: true, text: `Progress report ${inText(report)}` });
  if (bal < 0) out.push({ late: true, col: true, text: `Budget overspent by ${money(-bal)}` });
  if (isLive && age != null && age > 30) out.push({ late: true, col: true, text: `No note for ${age} days` });
  if (review != null && review < 0) out.push({ text: `Plan review ${inText(review)}` });
  if (Number(sc.overdue_tasks) > 0) out.push({ text: `${sc.overdue_tasks} overdue task${Number(sc.overdue_tasks) === 1 ? '' : 's'}` });
  if (isLive && age == null) out.push({ col: true, text: 'No notes yet' });
  else if (isLive && age > 14 && age <= 30) out.push({ col: true, text: `No note for ${age} days` });
  if (bal >= 0 && budget > 0 && (Number(sc.total_allocated) || 0) / budget >= 0.85) out.push({ text: 'Budget 85% used' });
  if (Number(sc.open_complaints) > 0) out.push({ text: `${sc.open_complaints} open complaint${Number(sc.open_complaints) === 1 ? '' : 's'}` });
  if (Number(sc.open_changes) > 0) out.push({ text: `${sc.open_changes} change${Number(sc.open_changes) === 1 ? '' : 's'} of circumstances open` });
  if (report != null && report >= 0 && report <= 30) out.push({ soon: true, text: `Progress report due ${inText(report)}` });
  return out;
}
const needsAttention = (sc) => sc.is_active && attention(sc).some((a) => !a.soon);
const reportDue = (sc) => { const d = daysFromToday(sc.progress_report_due); return d != null && d <= 30 && sc.is_active; };
const IN_VIEW = { active: live, attention: needsAttention, report: reportDue, all: () => true };

const COLS = 'md:grid md:grid-cols-[minmax(0,2.4fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,1.1fr)_auto] md:gap-4 md:items-center';

export default function Caseload({ clients = [], staff = [] }) {
  const navigate = useNavigate();
  const logTime = useLogTime();
  const list = useLoad('/support-coordination');
  const oversight = useLoad('/support-coordination/dashboard/oversight');
  const [q, setQ] = useState('');
  const [view, setView] = useState('active');
  const [tool, setTool] = useState(null);   // 'new' | 'bulk' | 'calc' | 'supervision'
  const all = useMemo(() => list.data || [], [list.data]);

  const counts = useMemo(() => Object.fromEntries(Object.entries(IN_VIEW).map(([k, f]) => [k, all.filter(f).length])), [all]);
  const lateCount = useMemo(() => all.filter((sc) => sc.is_active && attention(sc).some((a) => a.late)).length, [all]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    const rows = all.filter((sc) => IN_VIEW[view](sc))
      .filter((sc) => !s || `${sc.first_name || ''} ${sc.last_name || ''} ${sc.name || ''} ${sc.ndis_number || ''}`.toLowerCase().includes(s));
    const byName = (a, b) => `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(`${b.first_name || ''} ${b.last_name || ''}`);
    if (view === 'attention') {
      const weight = (sc) => attention(sc).reduce((t, a) => t + (a.late ? 10 : a.soon ? 0 : 1), 0);
      return rows.sort((a, b) => weight(b) - weight(a) || byName(a, b));
    }
    if (view === 'report') return rows.sort((a, b) => String(a.progress_report_due).localeCompare(String(b.progress_report_due)));
    return rows.sort(byName);
  }, [all, view, q]);

  const ov = oversight.data || {};
  const ovTotal = ['missingNotes', 'overdueTasks', 'budgetsAtRisk', 'openComplaints', 'expiringConsents'].reduce((t, k) => t + ((ov[k] && ov[k].length) || 0), 0);

  // The caseload as shown (view and search applied) as a CSV, for a supervisor or a handover.
  const exportCsv = () => {
    const text = toCsv(shown, [
      { label: 'Participant', get: (sc) => fullName(sc.first_name, sc.last_name) || sc.name },
      { label: 'NDIS number', get: (sc) => sc.ndis_number },
      { label: 'Status', get: (sc) => (sc.is_active === false ? 'Inactive' : words(sc.status)) },
      { label: 'Funding', get: (sc) => fundingLabel(sc.funding_source) },
      { label: 'Coordinator', get: (sc) => fullName(sc.coordinator_first, sc.coordinator_last) },
      { label: 'Plan ends', get: (sc) => (sc.plan_end ? String(sc.plan_end).slice(0, 10) : '') },
      { label: 'Last note', get: (sc) => (sc.last_note ? String(sc.last_note).slice(0, 10) : '') },
      { label: 'Budget', get: (sc) => sc.total_budget },
      { label: 'Used', get: (sc) => sc.total_allocated },
      { label: 'Left', get: (sc) => sc.total_balance },
      { label: 'Needs attention', get: (sc) => attention(sc).filter((a) => !a.soon).map((a) => a.text).join('; ') },
    ]);
    const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `sc-caseload-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const options = [
    { value: 'active', label: 'Active', count: counts.active },
    { value: 'attention', label: 'Needs attention', count: counts.attention, alert: lateCount > 0 },
    { value: 'report', label: 'Report due', count: counts.report },
    { value: 'all', label: 'All', count: counts.all },
  ];

  return (
    <div>
      <PageHeader title="Caseload" sub="Your participants, when you last wrote a note and what is coming up."
        actions={(
          <>
            <Button variant="secondary" icon={Plus} onClick={() => setTool('new')}>New participant</Button>
            <Button icon={Clock} onClick={() => logTime({})}>Log time</Button>
            <RowMenu label="More caseload tools" items={[
              { label: 'Export the list (CSV)', icon: Download, onClick: exportCsv, disabled: !shown.length },
              'sep',
              { label: 'Email or SMS several participants', icon: Send, onClick: () => setTool('bulk') },
              { label: 'Budget calculator', icon: Wallet, onClick: () => setTool('calc') },
              { label: 'Supervision and CPD', icon: ClipboardCheck, onClick: () => setTool('supervision') },
            ]} />
          </>
        )} />

      {ovTotal > 0 && (
        <p className="-mt-2 mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-slate-600">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" aria-hidden />
          <span>{ovTotal} {ovTotal === 1 ? 'item needs' : 'items need'} attention across the caseload.</span>
          <Link to="/support-coordination?view=dashboard&tab=oversight"
            className="inline-flex items-center font-semibold text-indigo-700 hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
            Reports and oversight<ChevronRight size={14} aria-hidden />
          </Link>
        </p>
      )}

      <Panel pad={false}>
        <div className="px-4 sm:px-5 py-3.5 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <SearchInput value={q} onChange={setQ} placeholder="Search participants" className="w-full sm:w-64" />
          <ViewPicker label="Show participants" value={view} onChange={setView} options={options} />
          {list.data && <p className="text-[12.5px] text-slate-500 sm:ml-auto" aria-live="polite">{shown.length} of {all.length}</p>}
        </div>

        {list.error ? <div className="p-5"><ErrorBlock error={list.error} onRetry={list.reload} /></div>
          : !list.data ? <div className="px-5 py-3"><LoadingBlock rows={6} /></div>
          : !all.length ? (
            <Empty icon={Users} title="No participants yet"
              action={<Button icon={Plus} onClick={() => setTool('new')}>New participant</Button>}>
              Add a participant to start their file: plan, budget, notes and claims all sit in it.
            </Empty>
          ) : !shown.length ? (
            <Empty icon={Search} title={q.trim() ? `No participants match “${q.trim()}”` : 'No participants in this view'}
              action={<Button variant="secondary" size="sm" onClick={() => { setQ(''); setView('all'); }}>Show everyone</Button>}>
              Try another view, or clear the search.
            </Empty>
          ) : (
            <>
              <div className={clsx('hidden px-5 py-2 bg-slate-50/80 border-b border-slate-100 text-[12px] font-semibold text-slate-600', COLS)} aria-hidden>
                <span>Participant</span><span>Plan ends</span><span>Last note</span><span>Budget left</span><span>Coordinator</span><span className="w-[124px]" />
              </div>
              <ul aria-label="Participants" className="divide-y divide-slate-100">
                {shown.map((sc) => (
                  <CaseRow key={sc.id} sc={sc}
                    onLog={() => logTime({ coordinationId: sc.id, participantName: fullName(sc.first_name, sc.last_name) || sc.name })} />
                ))}
              </ul>
            </>
          )}
        {list.loading && list.data && <p className="px-5 py-2 text-[12px] text-slate-500">Refreshing…</p>}
      </Panel>

      {tool === 'new' && <SCModal clients={clients} staff={staff} onClose={() => setTool(null)}
        onSaved={(id) => { setTool(null); list.reload(); if (id) navigate(`/support-coordination/${id}`); }} />}
      {tool === 'bulk' && <BulkMessageModal scList={all} onClose={() => setTool(null)} />}
      {tool === 'calc' && <BudgetCalculatorModal onClose={() => setTool(null)} />}
      {tool === 'supervision' && <SupervisionModal staff={staff} onClose={() => setTool(null)} />}
    </div>
  );
}

function CaseRow({ sc, onLog }) {
  const name = fullName(sc.first_name, sc.last_name) || sc.name || 'Participant';
  const items = attention(sc).filter((a) => !a.col);
  const top = items.slice(0, 2);
  const anyLate = top.some((a) => a.late);
  const age = noteAge(sc);
  const isLive = live(sc);
  const noteLate = isLive && age != null && age > 30;
  const bal = Number(sc.total_balance) || 0;
  const budget = Number(sc.total_budget) || 0;
  const planDays = daysFromToday(sc.plan_end);
  const coordinator = fullName(sc.coordinator_first, sc.coordinator_last);
  const cell = 'text-[13px] text-slate-700 md:min-w-0';
  const lbl = 'text-slate-500 md:sr-only';
  const logBtn = <Button variant="soft" size="sm" icon={Clock} onClick={onLog} aria-label={`Log time for ${name}`}>Log time</Button>;
  return (
    <li className={clsx('relative px-4 sm:px-5 py-3.5 md:py-3 hover:bg-slate-50/70 transition-colors', COLS, !sc.is_active && 'opacity-70')}>
      <div className="flex items-start gap-3 min-w-0">
        <Avatar first={sc.first_name} last={sc.last_name} size={32} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {/* The name is the row's link; it stretches over the row so the whole row opens the file. */}
            <Link to={`/support-coordination/${sc.id}`}
              className="font-semibold text-[14px] text-slate-900 hover:text-indigo-700 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 after:absolute after:inset-0 after:content-['']">
              {name}
            </Link>
            {sc.status !== 'active' && <Pill status={sc.status} size="xs" />}
            {!sc.is_active && sc.status === 'active' && <Pill tone="slate" label="Inactive" size="xs" />}
          </div>
          <p className="text-[12.5px] text-slate-500">{fundingLabel(sc.funding_source)}{sc.ndis_number ? ` · ${sc.ndis_number}` : ''}</p>
          {top.length > 0 && (
            <p className={clsx('mt-0.5 text-[12.5px] flex items-start gap-1', anyLate ? 'font-semibold text-rose-700' : 'text-amber-800')}>
              {anyLate && <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" aria-hidden />}
              <span>{top.map((a) => a.text).join(' · ')}{items.length > 2 ? ` · ${items.length - 2} more` : ''}</span>
            </p>
          )}
        </div>
        <div className="md:hidden relative z-[1] flex-shrink-0">{logBtn}</div>
      </div>

      <div className="mt-2 pl-11 md:pl-0 md:mt-0 flex flex-wrap gap-x-4 gap-y-1 md:contents">
        <p className={cell}>
          <span className={lbl}>Plan ends </span>
          {sc.plan_end ? (
            <span className={clsx('tabular-nums', planDays != null && planDays >= 0 && planDays <= 30 && 'font-semibold text-amber-800')}>
              {planDays != null && planDays < 0 ? `Ended ${fmtDate(sc.plan_end)}` : fmtDate(sc.plan_end)}
            </span>
          ) : <span className="text-slate-500">Not set</span>}
        </p>
        <p className={cell}>
          <span className={lbl}>Last note </span>
          {age == null ? <span className={isLive ? 'text-amber-800' : 'text-slate-500'}>No notes yet</span>
            : (
              <span className={clsx(noteLate ? 'font-semibold text-rose-700' : isLive && age > 14 && 'text-amber-800')}>
                {noteLate && <AlertTriangle size={12} className="inline -mt-0.5 mr-1" aria-hidden />}
                {agoText(age).replace(/^./, (c) => c.toUpperCase())}
              </span>
            )}
        </p>
        <p className={cell}>
          <span className={lbl}>Budget left </span>
          {budget <= 0 && bal === 0 ? <span className="text-slate-500">No budget</span> : (
            <span className={clsx('tabular-nums', bal < 0 ? 'font-semibold text-rose-700' : 'text-slate-800')}>
              {bal < 0 && <AlertTriangle size={12} className="inline -mt-0.5 mr-1" aria-hidden />}
              {bal < 0 ? `Over by ${money(-bal)}` : money(bal)}
            </span>
          )}
        </p>
        <p className={clsx(cell, 'md:truncate')}>
          <span className={lbl}>Coordinator </span>
          {coordinator || <span className="text-slate-500">Unassigned</span>}
        </p>
      </div>

      <div className="hidden md:flex relative z-[1] items-center justify-end gap-1 w-[124px]">
        {logBtn}
        <ChevronRight size={16} className="text-slate-400" aria-hidden />
      </div>
    </li>
  );
}
